import { call, put, select } from 'redux-saga/effects'

import {
  fetchMapProperties,
  fetchMapPropertiesAuthorized,
  type FetchMapPropertiesResponse,
} from './requests'
import type { FetchMapPropertiesRequestAction } from './actions'
import { fetchMapPropertiesFailureAction, fetchMapPropertiesSuccessAction } from './actions'
import { fetchPropertiesSuccessAction } from 'src/effects/Properties/actions'
import { getPropertiesKafkaSchema } from 'client/contexts/Tracking/utils'
import { loadMapSearchResults } from 'client/contexts/Tracking/events'
import { mapDataSearchMapToPropertiesCollection } from './mappers'
import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import { getProperty } from 'client/contexts/Property/selectors'
import type { Property } from 'src/pages/api/trpc/property/schema'

function isGenericError<TSuccess, TError extends { errorCode: string }>(
  data: TSuccess | TError,
  status: number
): data is TError {
  return status >= 400
}

const decimalPlaces = 6
export function normalizeLongitude(longitude: number): number {
  const normalized = ((((longitude + 180) % 360) + 360) % 360) - 180
  return Math.round(normalized * 10 ** decimalPlaces) / 10 ** decimalPlaces
}

export function normalizeLatitude(latitude: number): number {
  // Clamp latitude to valid range [-90, 90] instead of wrapping
  const clamped = Math.max(-90, Math.min(90, latitude))
  return Math.round(clamped * 10 ** decimalPlaces) / 10 ** decimalPlaces
}

export function* fetchMapPropertiesSaga(action: FetchMapPropertiesRequestAction): Generator {
  const { payload } = action

  const map = (yield select(({ map: m }) => m)) as FetchMapPropertiesResponse['data'] | undefined

  try {
    const property = (yield select(getProperty)) as Property
    if (property?.id) {
      payload.highlightedProperty = parseInt(`${property.id}`)
    }

    if (!payload.ignoreProperty && !payload.firstSearchDone) {
      if (property?.id) {
        payload.latitude = parseFloat(`${property.latitude}`)
        payload.longitude = parseFloat(`${property.longitude}`)
      }
    }

    if (payload.keepProperties && (map?.properties?.length ?? 0) > 0) {
      payload.excludedIDs = map?.properties?.map(p => p.id)
    }

    if (payload.longitude !== undefined) {
      payload.longitude = normalizeLongitude(payload.longitude)
    }

    if (payload.latitude !== undefined) {
      payload.latitude = normalizeLatitude(payload.latitude)
    }

    if (payload.boxShape !== undefined) {
      payload.boxShape = {
        topLeft: {
          latitude: normalizeLatitude(payload.boxShape.topLeft.latitude),
          longitude: normalizeLongitude(payload.boxShape.topLeft.longitude),
        },
        bottomRight: {
          latitude: normalizeLatitude(payload.boxShape.bottomRight.latitude),
          longitude: normalizeLongitude(payload.boxShape.bottomRight.longitude),
        },
      }
    }

    const request = payload?.isLogged ? fetchMapPropertiesAuthorized : fetchMapProperties

    const { data, status } = (yield call(request, payload)) as Awaited<ReturnType<typeof request>>

    if (isGenericError(data, status)) {
      yield put(fetchMapPropertiesFailureAction({ error: data.errorCode }))
      return
    }

    const { properties, metadata } = data
    const propertiesMapped = mapDataSearchMapToPropertiesCollection(properties)
    const distribution = metadata?.price?.distribution

    yield put(
      fetchMapPropertiesSuccessAction({
        properties: propertiesMapped,
        keepProperties: !!payload.keepProperties,
      })
    )
    yield put(fetchPropertiesSuccessAction({ distribution }))

    const trackingData = getPropertiesKafkaSchema(propertiesMapped)
    yield put(sendTrackingRequestAction({ event: loadMapSearchResults(trackingData) }))
  } catch (error) {
    yield put(
      fetchMapPropertiesFailureAction({
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    )
  }
}
